import React, { useState, useEffect, useRef } from "react";

import * as Yup from "yup";
import { Formik, FieldArray, Form, Field } from "formik";
import { toast } from "react-toastify";

import { makeStyles } from "@material-ui/core/styles";
import { green } from "@material-ui/core/colors";
import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import Typography from "@material-ui/core/Typography";
import IconButton from "@material-ui/core/IconButton";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import CircularProgress from "@material-ui/core/CircularProgress";
import Autocomplete from "@material-ui/lab/Autocomplete";

import { i18n } from "../../translate/i18n";

import api from "../../services/api";
import toastError from "../../errors/toastError";

const useStyles = makeStyles(theme => ({
	root: {
		display: "flex",
		flexWrap: "wrap",
	},
	textField: {
		marginRight: theme.spacing(1),
		flex: 1,
	},

	extraAttr: {
		display: "flex",
		justifyContent: "center",
		alignItems: "center",
	},

	btnWrapper: {
		position: "relative",
	},

	osField: {
		marginTop: theme.spacing(1),
	},

	buttonProgress: {
		color: green[500],
		position: "absolute",
		top: "50%",
		left: "50%",
		marginTop: -12,
		marginLeft: -12,
	},
}));

const ContactSchema = Yup.object().shape({
	name: Yup.string()
		.min(2, "Too Short!")
		.max(50, "Too Long!")
		.required("Required"),
	number: Yup.string().min(8, "Too Short!").max(50, "Too Long!"),
	email: Yup.string().email("Invalid email"),
});

const ContactModal = ({ open, onClose, contactId, initialValues, onSave }) => {
	const classes = useStyles();
	const isMounted = useRef(true);

	const initialState = {
		name: "",
		number: "",
		email: "",
		osTerceiroId: null,
		osContatoId: null,
	};

	const [contact, setContact] = useState(initialState);
	const [osClientes, setOsClientes] = useState([]);
	const [osCliente, setOsCliente] = useState(null);
	const [osContatos, setOsContatos] = useState([]);
	const [osContato, setOsContato] = useState(null);
	const [osClienteSearch, setOsClienteSearch] = useState("");
	const [loadingOsClientes, setLoadingOsClientes] = useState(false);
	const [loadingOsContatos, setLoadingOsContatos] = useState(false);
	const [creatingOsContato, setCreatingOsContato] = useState(false);

	useEffect(() => {
		return () => {
			isMounted.current = false;
		};
	}, []);

	useEffect(() => {
		if (!open || osClienteSearch.length < 3) {
			setLoadingOsClientes(false);
			return;
		}

		setLoadingOsClientes(true);
		const timeout = setTimeout(async () => {
			try {
				const { data } = await api.get("/os/clientes", {
					params: { q: osClienteSearch, per_page: 10 },
				});
				setOsClientes(data.data || []);
			} catch (err) {
				toastError(err);
			} finally {
				setLoadingOsClientes(false);
			}
		}, 500);

		return () => clearTimeout(timeout);
	}, [open, osClienteSearch]);

	useEffect(() => {
		const fetchContact = async () => {
			if (!open) return;
			if (initialValues) {
				setContact(prevState => {
					return { ...prevState, ...initialValues };
				});
			}

			if (!contactId) return;

			try {
				const { data } = await api.get(`/contacts/${contactId}`);
				if (isMounted.current) {
					setContact(data);
				}

				if (data.osTerceiroId) {
					const [clienteResponse, contatosResponse] = await Promise.all([
						api.get(`/os/clientes/${data.osTerceiroId}`),
						api.get(`/os/terceiros/${data.osTerceiroId}/contatos`, {
							params: { per_page: 100 },
						}),
					]);
					const cliente = clienteResponse.data.data || clienteResponse.data;
					const contatos = contatosResponse.data.data || [];
					setOsCliente(cliente);
					setOsClientes([cliente]);
					setOsContatos(contatos);
					setOsContato(contatos.find(item => item.id === data.osContatoId) || null);
				}
			} catch (err) {
				toastError(err);
			}
		};

		fetchContact();
	}, [contactId, open, initialValues]);

	const handleClose = () => {
		onClose();
		setContact(initialState);
		setOsClientes([]);
		setOsCliente(null);
		setOsContatos([]);
		setOsContato(null);
		setOsClienteSearch("");
	};

	const handleOsClienteChange = async newValue => {
		setOsCliente(newValue);
		setOsContato(null);
		setOsContatos([]);

		if (!newValue) return;

		setLoadingOsContatos(true);
		try {
			const { data } = await api.get(`/os/terceiros/${newValue.id}/contatos`, {
				params: { per_page: 100 },
			});
			setOsContatos(data.data || []);
		} catch (err) {
			toastError(err);
		} finally {
			setLoadingOsContatos(false);
		}
	};

	const handleCreateOsContato = async values => {
		if (!osCliente) {
			toast.error(i18n.t("contactModal.os.selectCompany"));
			return;
		}

		setCreatingOsContato(true);
		try {
			const { data } = await api.post(`/os/terceiros/${osCliente.id}/contatos`, {
				contato: values.name,
				numero: values.number,
				contactId,
			});
			const created = data.data || data;
			setOsContatos(previous => [...previous, created]);
			setOsContato(created);
			toast.success(i18n.t("contactModal.os.created"));
		} catch (err) {
			toastError(err);
		} finally {
			setCreatingOsContato(false);
		}
	};

	const handleSaveContact = async values => {
		const contactData = {
			...values,
			osTerceiroId: osCliente?.id || null,
			osContatoId: osContato?.id || null,
		};

		try {
			if (contactId) {
				await api.put(`/contacts/${contactId}`, contactData);
				handleClose();
			} else {
				const { data } = await api.post("/contacts", contactData);
				if (onSave) {
					onSave(data);
				}
				handleClose();
			}
			toast.success(i18n.t("contactModal.success"));
		} catch (err) {
			toastError(err);
		}
	};

	return (
		<div className={classes.root}>
			<Dialog open={open} onClose={handleClose} maxWidth="lg" scroll="paper">
				<DialogTitle id="form-dialog-title">
					{contactId
						? `${i18n.t("contactModal.title.edit")}`
						: `${i18n.t("contactModal.title.add")}`}
				</DialogTitle>
				<Formik
					initialValues={contact}
					enableReinitialize={true}
					validationSchema={ContactSchema}
					onSubmit={(values, actions) => {
						setTimeout(() => {
							handleSaveContact(values);
							actions.setSubmitting(false);
						}, 400);
					}}
				>
					{({ values, errors, touched, isSubmitting }) => (
						<Form>
							<DialogContent dividers>
								<Typography variant="subtitle1" gutterBottom>
									{i18n.t("contactModal.form.mainInfo")}
								</Typography>
								<Field
									as={TextField}
									label={i18n.t("contactModal.form.name")}
									name="name"
									autoFocus
									error={touched.name && Boolean(errors.name)}
									helperText={touched.name && errors.name}
									variant="outlined"
									margin="dense"
									className={classes.textField}
								/>
								<Field
									as={TextField}
									label={i18n.t("contactModal.form.number")}
									name="number"
									error={touched.number && Boolean(errors.number)}
									helperText={touched.number && errors.number}
									placeholder="5513912344321"
									variant="outlined"
									margin="dense"
								/>
								<div>
									<Field
										as={TextField}
										label={i18n.t("contactModal.form.email")}
										name="email"
										error={touched.email && Boolean(errors.email)}
										helperText={touched.email && errors.email}
										placeholder="Email address"
										fullWidth
										margin="dense"
										variant="outlined"
									/>
								</div>

								<Typography
									style={{ marginBottom: 4, marginTop: 12 }}
									variant="subtitle1"
								>
									{i18n.t("contactModal.os.title")}
								</Typography>
								<Autocomplete
									fullWidth
									options={osClientes}
									loading={loadingOsClientes}
									value={osCliente}
									onChange={(event, newValue) => handleOsClienteChange(newValue)}
									onInputChange={(event, value, reason) => {
										if (reason === "input") setOsClienteSearch(value);
									}}
									getOptionSelected={(option, value) => option.id === value.id}
									getOptionLabel={option => {
										if (!option) return "";
										return `${option.razao || option.nome || option.fantasia || i18n.t("contactModal.os.company")} - ${option.cnpj_cpf || ""}`;
									}}
									renderInput={params => (
										<TextField
											{...params}
											label={i18n.t("contactModal.os.company")}
											variant="outlined"
											margin="dense"
											helperText={i18n.t("contactModal.os.companyHint")}
											InputProps={{
												...params.InputProps,
												endAdornment: (
													<>
														{loadingOsClientes ? <CircularProgress color="inherit" size={20} /> : null}
														{params.InputProps.endAdornment}
													</>
												),
											}}
										/>
									)}
								/>
								<Autocomplete
									fullWidth
									className={classes.osField}
									options={osContatos}
									loading={loadingOsContatos}
									value={osContato}
									disabled={!osCliente}
									onChange={(event, newValue) => setOsContato(newValue)}
									getOptionSelected={(option, value) => option.id === value.id}
									getOptionLabel={option => option ? `${option.contato} - ${option.fone}` : ""}
									renderInput={params => (
										<TextField
											{...params}
											label={i18n.t("contactModal.os.contact")}
											variant="outlined"
											margin="dense"
											InputProps={{
												...params.InputProps,
												endAdornment: (
													<>
														{loadingOsContatos ? <CircularProgress color="inherit" size={20} /> : null}
														{params.InputProps.endAdornment}
													</>
												),
											}}
										/>
									)}
								/>
								<Button
									type="button"
									fullWidth
									className={classes.osField}
									variant="outlined"
									color="primary"
									disabled={!osCliente || creatingOsContato || !values.name || !values.number}
									onClick={() => handleCreateOsContato(values)}
								>
									{creatingOsContato
										? <CircularProgress size={20} />
										: i18n.t("contactModal.os.create")}
								</Button>

								<Typography
									style={{ marginBottom: 8, marginTop: 12 }}
									variant="subtitle1"
								>
									{i18n.t("contactModal.form.extraInfo")}
								</Typography>

								<FieldArray name="extraInfo">
									{({ push, remove }) => (
										<>
											{values.extraInfo &&
												values.extraInfo.length > 0 &&
												values.extraInfo.map((info, index) => (
													<div
														className={classes.extraAttr}
														key={`${index}-info`}
													>
														<Field
															as={TextField}
															label={i18n.t("contactModal.form.extraName")}
															name={`extraInfo[${index}].name`}
															variant="outlined"
															margin="dense"
															className={classes.textField}
														/>
														<Field
															as={TextField}
															label={i18n.t("contactModal.form.extraValue")}
															name={`extraInfo[${index}].value`}
															variant="outlined"
															margin="dense"
															className={classes.textField}
														/>
														<IconButton
															size="small"
															onClick={() => remove(index)}
														>
															<DeleteOutlineIcon />
														</IconButton>
													</div>
												))}
											<div className={classes.extraAttr}>
												<Button
													style={{ flex: 1, marginTop: 8 }}
													variant="outlined"
													color="primary"
													onClick={() => push({ name: "", value: "" })}
												>
													{`+ ${i18n.t("contactModal.buttons.addExtraInfo")}`}
												</Button>
											</div>
										</>
									)}
								</FieldArray>
							</DialogContent>
							<DialogActions>
								<Button
									onClick={handleClose}
									color="secondary"
									disabled={isSubmitting}
									variant="outlined"
								>
									{i18n.t("contactModal.buttons.cancel")}
								</Button>
								<Button
									type="submit"
									color="primary"
									disabled={isSubmitting}
									variant="contained"
									className={classes.btnWrapper}
								>
									{contactId
										? `${i18n.t("contactModal.buttons.okEdit")}`
										: `${i18n.t("contactModal.buttons.okAdd")}`}
									{isSubmitting && (
										<CircularProgress
											size={24}
											className={classes.buttonProgress}
										/>
									)}
								</Button>
							</DialogActions>
						</Form>
					)}
				</Formik>
			</Dialog>
		</div>
	);
};

export default ContactModal;
